import React, { type ReactElement } from 'react';
import { Box, Icon, InlineMessage, Switch } from '@saviynt/design-system';

import { FieldLabel } from '../../../../shared/components';
import { testIds, VALIDATION_TYPES } from '../../../../shared/constants';
import { type BooleanField } from '../../../../shared/types';

import styles from './PreviewFields.module.css';

const parseBool = (v: unknown): boolean => {
  if (typeof v === 'boolean') {
    return v;
  }

  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();

    return s === 'true' || s === '1';
  }

  return false;
};

interface BooleanPreviewProps {
  field: BooleanField;
  isChecked: boolean | null;
  onChange?: (value: boolean) => void;
  error?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
}

function BooleanSwitch({
  field,
  isChecked,
  onChange,
  error,
  isRequired: isRequiredProp,
  isReadOnly: isReadOnlyProp,
}: BooleanPreviewProps): ReactElement {
  const { label, helperText, infoTooltipText, validations, isReadOnly } = field;

  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;

  const isReadOnlyResult = isReadOnly || isReadOnlyProp || false;
  const groupBottomText = error ?? helperText;
  const resolvedChecked = isChecked ?? parseBool(field.defaultValue?.value);

  const handleToggle = () => {
    onChange?.(!resolvedChecked);
  };

  return (
    <Box className={styles.booleanSwitch}>
      <FieldLabel
        label={label}
        isReadOnly={isReadOnlyResult}
        infoTooltipText={infoTooltipText}
        isRequired={isRequired}
        isCritical={!!error}
        shouldRequiredAsterisk={isChecked === null && isRequired}
      />
      <Switch
        isChecked={resolvedChecked}
        isDisabled={isReadOnlyResult}
        onChange={handleToggle}
        tabIndex={0}
        className={styles.booleanSwitchControl}
        dataTestId={testIds.FF_PREVIEW_BOOLEAN_SWITCH_FIELD}
      />
      {(error || helperText) && (
        <InlineMessage
          text={groupBottomText}
          colorTheme={error ? 'critical' : 'neutral'}
          size='medium'
          leftIcon={error ? <Icon kind='AlertCriticalFilled' /> : undefined}
        />
      )}
    </Box>
  );
}

export default BooleanSwitch;
